import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";

// ── Utility ───────────────────────────────────────────────────────────────────

async function apiFetch(path) {
  const { data } = await api.get(path);
  return data;
}

function fmt(val, dp = 2) {
  const n = parseFloat(val);
  if (isNaN(n)) return "—";
  return n.toLocaleString("en-GB", { minimumFractionDigits: dp, maximumFractionDigits: dp });
}

function fmtCurrency(val, currency = "£", dp = 2) {
  return `${currency}${fmt(val, dp)}`;
}

function fmtPct(val) {
  const n = parseFloat(val);
  if (isNaN(n)) return "—";
  return `${n.toFixed(2)}%`;
}

function marginClass(val) {
  const n = parseFloat(val);
  if (isNaN(n)) return "";
  if (n >= 30) return "profit-positive";
  if (n >= 0) return "profit-neutral";
  return "profit-negative";
}

// ── Sub-components ────────────────────────────────────────────────────────────

function KPICard({ label, value, sub, accent }) {
  return (
    <div className={`kpi-card${accent ? " kpi-accent" : ""}`}>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
      {sub && <div className="kpi-sub">{sub}</div>}
    </div>
  );
}

function SectionHeader({ title, action }) {
  return (
    <div className="section-header">
      <h2>{title}</h2>
      {action}
    </div>
  );
}

function ErrorBanner({ message, onRetry }) {
  return (
    <div className="error-banner" role="alert">
      <span>{message}</span>
      {onRetry && (
        <button className="retry-btn" onClick={onRetry}>
          Retry
        </button>
      )}
    </div>
  );
}

function Spinner() {
  return <div className="spinner" aria-label="Loading…" />;
}

function EmptyState({ message }) {
  return <div className="empty-state">{message}</div>;
}

function CorridorTable({ rows }) {
  if (!rows.length) return <EmptyState message="No corridor data yet." />;
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Corridor</th>
            <th className="num">Revenue</th>
            <th className="num">Cost</th>
            <th className="num">Gross Profit</th>
            <th className="num">Margin</th>
            <th className="num">Transfers</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.corridor}>
              <td>
                <span className="corridor-tag">{r.corridor}</span>
              </td>
              <td className="num">{fmtCurrency(r.total_revenue)}</td>
              <td className="num">{fmtCurrency(r.total_cost)}</td>
              <td className={`num ${marginClass(parseFloat(r.gross_profit) >= 0 ? 30 : -1)}`}>
                {fmtCurrency(r.gross_profit)}
              </td>
              <td className={`num ${marginClass(r.avg_margin_pct)}`}>
                {fmtPct(r.avg_margin_pct)}
              </td>
              <td className="num">{r.count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ProviderTable({ rows, type }) {
  if (!rows.length) return <EmptyState message={`No ${type} provider data yet.`} />;
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Provider</th>
            <th className="num">Total Cost</th>
            <th className="num">Total Revenue</th>
            <th className="num">Profit</th>
            <th className="num">Rebates</th>
            <th className="num">Transfers</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.provider}>
              <td>
                <span className="provider-tag">{r.provider || "—"}</span>
              </td>
              <td className="num">{fmtCurrency(r.total_cost)}</td>
              <td className="num">{fmtCurrency(r.total_revenue)}</td>
              <td className="num">{fmtCurrency(r.total_profit)}</td>
              <td className="num">{fmtCurrency(r.total_rebate)}</td>
              <td className="num">{r.count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CustomerTable({ rows }) {
  if (!rows.length) return <EmptyState message="No customer data yet." />;
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>User ID</th>
            <th>Type</th>
            <th className="num">Total Fees</th>
            <th className="num">Total Profit</th>
            <th className="num">Transfers</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r._id}>
              <td>
                <span className="user-id">{r._id}</span>
              </td>
              <td>
                <span className="type-badge">{r.customer_type || "personal"}</span>
              </td>
              <td className="num">{fmtCurrency(r.total_fees)}</td>
              <td className="num">{fmtCurrency(r.total_profit)}</td>
              <td className="num">{r.tx_count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DailyChart({ rows }) {
  if (!rows.length) return <EmptyState message="No daily data in this range." />;
  const maxVal = Math.max(...rows.map((r) => Math.abs(parseFloat(r.gross_profit) || 0)), 1);
  return (
    <div className="daily-chart" role="img" aria-label="Daily revenue chart">
      {rows.map((r) => {
        const gp = parseFloat(r.gross_profit) || 0;
        const rev = parseFloat(r.total_revenue) || 0;
        const barH = Math.round((Math.abs(gp) / maxVal) * 80);
        const positive = gp >= 0;
        return (
          <div className="chart-col" key={r.date} title={`${r.date}\nRevenue: £${fmt(rev)}\nProfit: £${fmt(gp)}\nTransfers: ${r.count}`}>
            <div className="chart-bar-wrap">
              <div
                className={`chart-bar ${positive ? "bar-positive" : "bar-negative"}`}
                style={{ height: `${barH}px` }}
              />
            </div>
            <div className="chart-label">{r.date.slice(5)}</div>
          </div>
        );
      })}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function AdminFinanceDashboard() {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [daily, setDaily] = useState([]);
  const [corridors, setCorridors] = useState([]);
  const [payoutProviders, setPayoutProviders] = useState([]);
  const [fundingProviders, setFundingProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [csvExporting, setCsvExporting] = useState(false);
  const [dateRange, setDateRange] = useState({ from: "", to: "" });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (dateRange.from) params.set("date_from", dateRange.from);
      if (dateRange.to) params.set("date_to", dateRange.to);
      const qs = params.toString() ? `?${params}` : "";

      const [dash, dailyData, corridorData, payoutData, fundingData] = await Promise.all([
        apiFetch("/finance/dashboard"),
        apiFetch(`/finance/revenue/daily${qs}`),
        apiFetch(`/finance/corridors${qs}`),
        apiFetch("/finance/providers?provider_type=payout"),
        apiFetch("/finance/providers?provider_type=funding"),
      ]);
      setDashboard(dash);
      setDaily(dailyData);
      setCorridors(corridorData);
      setPayoutProviders(payoutData);
      setFundingProviders(fundingData);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    load();
  }, [load]);

  const handleExportCSV = async () => {
    setCsvExporting(true);
    try {
      const params = new URLSearchParams();
      if (dateRange.from) params.set("date_from", dateRange.from);
      if (dateRange.to) params.set("date_to", dateRange.to);
      const qs = params.toString() ? `?${params}` : "";
      const res = await api.get(`/finance/export/csv${qs}`, { responseType: "blob" });
      const blob = res.data;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `moneymeet_revenue_export.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(`Export failed: ${e.message}`);
    } finally {
      setCsvExporting(false);
    }
  };

  const at = dashboard?.all_time || {};
  const today = dashboard?.today || {};
  const month = dashboard?.this_month || {};

  return (
    <div className="fin-dash" data-testid="admin-finance-dashboard">
      <style>{STYLES}</style>

      {/* Header */}
      <div className="fin-header">
        <div className="fin-header-left">
          <button className="back-btn" onClick={() => navigate("/admin")} aria-label="Back to admin">
            ← Admin
          </button>
          <div>
            <h1>Finance Dashboard</h1>
            <p className="fin-subtitle">Internal revenue analytics — admin only</p>
          </div>
        </div>
        <div className="fin-header-right">
          <div className="date-filters">
            <input
              type="date"
              value={dateRange.from}
              onChange={(e) => setDateRange((d) => ({ ...d, from: e.target.value }))}
              aria-label="Date from"
            />
            <span>—</span>
            <input
              type="date"
              value={dateRange.to}
              onChange={(e) => setDateRange((d) => ({ ...d, to: e.target.value }))}
              aria-label="Date to"
            />
            <button className="apply-btn" onClick={load}>
              Apply
            </button>
          </div>
          <button
            className="export-btn"
            onClick={handleExportCSV}
            disabled={csvExporting}
            data-testid="export-csv-btn"
          >
            {csvExporting ? "Exporting…" : "Export CSV"}
          </button>
        </div>
      </div>

      {error && <ErrorBanner message={error} onRetry={load} />}

      {loading ? (
        <div className="loading-state">
          <Spinner />
          <p>Loading financial data…</p>
        </div>
      ) : (
        <>
          {/* KPI strip */}
          <div className="kpi-strip">
            <KPICard
              label="All-time Revenue"
              value={fmtCurrency(at.total_revenue)}
              sub={`${at.count || 0} transfers`}
              accent
            />
            <KPICard
              label="All-time Gross Profit"
              value={fmtCurrency(at.gross_profit)}
              sub={`Net: ${fmtCurrency(at.net_profit)}`}
            />
            <KPICard
              label="Avg Gross Margin"
              value={fmtPct(at.avg_margin)}
              sub="All-time"
            />
            <KPICard
              label="Today's Revenue"
              value={fmtCurrency(today.total_revenue)}
              sub={`${today.count || 0} transfers`}
            />
            <KPICard
              label="This Month Revenue"
              value={fmtCurrency(month.total_revenue)}
              sub={`${month.count || 0} transfers`}
            />
            <KPICard
              label="Total Funding Cost"
              value={fmtCurrency(at.total_funding_cost)}
              sub="Provider charges"
            />
            <KPICard
              label="Total Payout Cost"
              value={fmtCurrency(at.total_payout_cost)}
              sub="Provider charges"
            />
            <KPICard
              label="Provider Rebates"
              value={fmtCurrency(at.total_rebates)}
              sub="Received"
            />
          </div>

          {/* Tab nav */}
          <div className="tab-nav" role="tablist">
            {["overview", "corridors", "providers", "customers"].map((t) => (
              <button
                key={t}
                role="tab"
                aria-selected={activeTab === t}
                className={`tab-btn${activeTab === t ? " tab-active" : ""}`}
                onClick={() => setActiveTab(t)}
              >
                {t.charAt(0).toUpperCase() + t.slice(1)}
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div className="tab-content">
            {activeTab === "overview" && (
              <div className="tab-pane">
                <SectionHeader title="Daily Revenue & Profit (last 30 days)" />
                <DailyChart rows={daily} />

                <SectionHeader title="Cost Breakdown (all-time)" />
                <div className="cost-grid">
                  {[
                    { label: "Funding Provider", val: at.total_funding_cost },
                    { label: "Payout Provider", val: at.total_payout_cost },
                    { label: "Infrastructure", val: at.total_infra_cost },
                    { label: "Total Cost", val: at.total_cost },
                  ].map(({ label, val }) => (
                    <div key={label} className="cost-card">
                      <div className="cost-label">{label}</div>
                      <div className="cost-value">{fmtCurrency(val)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === "corridors" && (
              <div className="tab-pane">
                <SectionHeader title="Corridor Profitability" />
                <CorridorTable rows={corridors} />
              </div>
            )}

            {activeTab === "providers" && (
              <div className="tab-pane">
                <SectionHeader title="Payout Providers" />
                <ProviderTable rows={payoutProviders} type="payout" />

                <SectionHeader title="Funding Providers" />
                <ProviderTable rows={fundingProviders} type="funding" />
              </div>
            )}

            {activeTab === "customers" && (
              <div className="tab-pane">
                <SectionHeader title="Top Customers by Revenue" />
                <CustomerTable rows={dashboard?.top_customers || []} />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const STYLES = `
.fin-dash {
  min-height: 100vh;
  background: var(--fin-bg, #f8f9fb);
  color: var(--fin-text, #1a1d23);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  font-size: 14px;
}

@media (prefers-color-scheme: dark) {
  .fin-dash { --fin-bg: #0f1117; --fin-text: #e8eaf0; --fin-card: #1a1d27;
    --fin-border: #2a2d3a; --fin-muted: #6b7280; --fin-accent: #6366f1;
    --fin-accent-bg: #1e1f36; }
}
:root[data-theme="dark"] .fin-dash { --fin-bg: #0f1117; --fin-text: #e8eaf0; --fin-card: #1a1d27;
  --fin-border: #2a2d3a; --fin-muted: #6b7280; --fin-accent: #6366f1;
  --fin-accent-bg: #1e1f36; }
:root[data-theme="light"] .fin-dash { --fin-bg: #f8f9fb; --fin-text: #1a1d23; --fin-card: #ffffff;
  --fin-border: #e2e5ed; --fin-muted: #6b7280; --fin-accent: #4f46e5;
  --fin-accent-bg: #eeeffc; }
.fin-dash {
  --fin-card: #ffffff; --fin-border: #e2e5ed; --fin-muted: #6b7280;
  --fin-accent: #4f46e5; --fin-accent-bg: #eeeffc;
}

/* Header */
.fin-header {
  display: flex; align-items: flex-start; justify-content: space-between;
  flex-wrap: wrap; gap: 16px;
  padding: 24px 28px 16px;
  border-bottom: 1px solid var(--fin-border);
  background: var(--fin-card);
}
.fin-header-left { display: flex; align-items: center; gap: 16px; }
.fin-header-left h1 { margin: 0; font-size: 22px; font-weight: 700; }
.fin-subtitle { margin: 2px 0 0; color: var(--fin-muted); font-size: 12px; }
.fin-header-right { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.back-btn {
  background: none; border: 1px solid var(--fin-border); border-radius: 6px;
  padding: 6px 12px; cursor: pointer; color: var(--fin-muted); font-size: 13px;
  white-space: nowrap;
}
.back-btn:hover { background: var(--fin-accent-bg); color: var(--fin-accent); }
.date-filters { display: flex; align-items: center; gap: 6px; }
.date-filters input {
  border: 1px solid var(--fin-border); border-radius: 6px;
  padding: 6px 10px; font-size: 13px; background: var(--fin-bg);
  color: var(--fin-text);
}
.apply-btn, .export-btn {
  border-radius: 6px; padding: 7px 14px; font-size: 13px;
  font-weight: 600; cursor: pointer; white-space: nowrap; border: none;
}
.apply-btn { background: var(--fin-accent-bg); color: var(--fin-accent); }
.apply-btn:hover { opacity: 0.85; }
.export-btn { background: var(--fin-accent); color: #fff; }
.export-btn:hover:not(:disabled) { opacity: 0.88; }
.export-btn:disabled { opacity: 0.5; cursor: not-allowed; }

/* KPI strip */
.kpi-strip {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 16px; padding: 24px 28px;
}
.kpi-card {
  background: var(--fin-card); border: 1px solid var(--fin-border);
  border-radius: 10px; padding: 16px 18px;
}
.kpi-accent { border-left: 3px solid var(--fin-accent); }
.kpi-label { font-size: 11px; font-weight: 600; text-transform: uppercase;
  letter-spacing: .6px; color: var(--fin-muted); margin-bottom: 6px; }
.kpi-value { font-size: 20px; font-weight: 700; font-variant-numeric: tabular-nums; }
.kpi-sub { font-size: 11px; color: var(--fin-muted); margin-top: 4px; }

/* Tabs */
.tab-nav {
  display: flex; gap: 4px; padding: 0 28px;
  border-bottom: 1px solid var(--fin-border);
}
.tab-btn {
  background: none; border: none; border-bottom: 2px solid transparent;
  padding: 10px 16px; font-size: 13px; font-weight: 500;
  color: var(--fin-muted); cursor: pointer; margin-bottom: -1px;
}
.tab-btn:hover { color: var(--fin-text); }
.tab-active { color: var(--fin-accent) !important; border-bottom-color: var(--fin-accent) !important; }

.tab-content { padding: 24px 28px; }
.tab-pane { display: flex; flex-direction: column; gap: 24px; }

.section-header {
  display: flex; align-items: center; justify-content: space-between;
  padding-bottom: 8px; border-bottom: 1px solid var(--fin-border);
}
.section-header h2 { margin: 0; font-size: 15px; font-weight: 600; }

/* Tables */
.table-wrap { overflow-x: auto; border-radius: 8px;
  border: 1px solid var(--fin-border); }
table { width: 100%; border-collapse: collapse; }
th, td { padding: 10px 14px; text-align: left; white-space: nowrap; }
th { font-size: 11px; font-weight: 700; text-transform: uppercase;
  letter-spacing: .5px; color: var(--fin-muted);
  background: var(--fin-bg); border-bottom: 1px solid var(--fin-border); }
td { border-bottom: 1px solid var(--fin-border); font-variant-numeric: tabular-nums; }
tr:last-child td { border-bottom: none; }
tr:hover td { background: var(--fin-accent-bg); }
.num { text-align: right; }

/* Tags */
.corridor-tag, .provider-tag {
  font-size: 12px; font-weight: 600; padding: 2px 8px;
  border-radius: 4px; background: var(--fin-accent-bg); color: var(--fin-accent);
  font-family: monospace;
}
.user-id { font-family: monospace; font-size: 12px; color: var(--fin-muted); }
.type-badge {
  font-size: 11px; padding: 2px 8px; border-radius: 10px;
  background: var(--fin-border); font-weight: 600;
}

/* Profit indicators */
.profit-positive { color: #16a34a; }
.profit-negative { color: #dc2626; }
.profit-neutral  { color: var(--fin-muted); }

/* Daily chart */
.daily-chart {
  display: flex; align-items: flex-end; gap: 4px;
  height: 120px; padding: 16px;
  background: var(--fin-card); border: 1px solid var(--fin-border);
  border-radius: 8px; overflow-x: auto;
}
.chart-col { display: flex; flex-direction: column; align-items: center; gap: 4px; min-width: 28px; }
.chart-bar-wrap { height: 80px; display: flex; align-items: flex-end; }
.chart-bar { width: 18px; border-radius: 3px 3px 0 0; min-height: 2px; }
.bar-positive { background: #4f46e5; }
.bar-negative { background: #dc2626; }
.chart-label { font-size: 9px; color: var(--fin-muted); }

/* Cost grid */
.cost-grid {
  display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 12px;
}
.cost-card {
  background: var(--fin-card); border: 1px solid var(--fin-border);
  border-radius: 8px; padding: 14px 16px;
}
.cost-label { font-size: 11px; font-weight: 600; text-transform: uppercase;
  letter-spacing: .5px; color: var(--fin-muted); margin-bottom: 4px; }
.cost-value { font-size: 18px; font-weight: 700; font-variant-numeric: tabular-nums; }

/* States */
.loading-state { display: flex; flex-direction: column; align-items: center;
  justify-content: center; gap: 16px; padding: 80px 24px; color: var(--fin-muted); }
.spinner {
  width: 32px; height: 32px; border: 3px solid var(--fin-border);
  border-top-color: var(--fin-accent); border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
.empty-state { text-align: center; padding: 32px; color: var(--fin-muted);
  background: var(--fin-card); border: 1px solid var(--fin-border);
  border-radius: 8px; font-size: 13px; }
.error-banner {
  display: flex; align-items: center; justify-content: space-between;
  margin: 16px 28px; padding: 12px 16px; background: #fef2f2;
  border: 1px solid #fecaca; border-radius: 8px; color: #dc2626; font-size: 13px;
}
.retry-btn { background: none; border: none; cursor: pointer;
  color: #dc2626; font-weight: 600; text-decoration: underline; }

@media (max-width: 640px) {
  .fin-header, .kpi-strip, .tab-content { padding-left: 16px; padding-right: 16px; }
  .tab-nav { padding-left: 16px; }
  .kpi-strip { grid-template-columns: 1fr 1fr; }
  .fin-header { flex-direction: column; }
  .fin-header-right { flex-wrap: wrap; }
  .date-filters { flex-wrap: wrap; }
}
`;
